import LoaderBig from "../Loader/LoaderBig";
import styles from "./Singlepost.module.css";

import { Fragment, useCallback, useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify";

const apiUrl = process.env.REACT_APP_SERVER_URL || "https://hkserver.vercel.app";

const Singlepost = () => {
  const [postsData, setPostData] = useState({
    post: { post: { user: {} } },
    createDate: "",
    updateDate: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);

  // Read Aloud state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedLang, setSelectedLang] = useState("");
  const [availableVoices, setAvailableVoices] = useState([]);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);
  const summaryFrameRef = useRef(null);

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight > 0) {
      setScrollProgress(Math.min((scrollTop / docHeight) * 100, 100));
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Load available speech synthesis voices
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        // Deduplicate by language code, keep one voice per lang
        const langMap = new Map();
        voices.forEach((v) => {
          if (!langMap.has(v.lang)) {
            langMap.set(v.lang, { lang: v.lang, name: v.name });
          }
        });
        const sorted = Array.from(langMap.values()).sort((a, b) =>
          a.lang.localeCompare(b.lang)
        );
        setAvailableVoices(sorted);
        if (!selectedLang && sorted.length > 0) {
          // Default to English or first available
          const english = sorted.find((v) => v.lang.startsWith("en"));
          setSelectedLang(english ? english.lang : sorted[0].lang);
        }
      }
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [selectedLang]);

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const queryString = window.location.search;
    const params = new URLSearchParams(queryString);
    let title = params.get("title");
    let id = params.get("id");
    const url = apiUrl + "/public/getsinglepost";
    if (title && id) {
      fetch(url, {
        method: "post",
        credentials: "include",
        body: JSON.stringify({
          title: title,
          postId: id,
          timeZone: userTimezone,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("no post available");
          }
          return response.json();
        })
        .then((data) => {
          if (data) {
            setPostData(data);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    }
  }, []);

  const currentUrl = window.location.href;
  const postTitle = postsData.post.post.title || "";
  const postId = postsData.post.post._id || "";
  const quickReadUrl = new URL("https://hackerblog-kappa.vercel.app/");
  quickReadUrl.searchParams.set("mode", "quick-read");
  if (postTitle) quickReadUrl.searchParams.set("title", postTitle);
  if (postId) quickReadUrl.searchParams.set("id", postId);

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(postTitle)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(postTitle + " " + currentUrl)}`,
    reddit: `https://reddit.com/submit?url=${encodeURIComponent(currentUrl)}&title=${encodeURIComponent(postTitle)}`,
  };

  const handleShare = (platform) => {
    const url = shareLinks[platform];
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=500");
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl).then(() => {
      setCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  const sanitizedContent = DOMPurify.sanitize(
    postsData.post.post.content || "",
    {
      USE_PROFILES: { html: true },
      FORBID_TAGS: [
        "script",
        "style",
        "iframe",
        "object",
        "embed",
        "form",
        "input",
        "button",
        "textarea",
        "select",
        "svg",
        "math",
      ],
      FORBID_ATTR: [
        "style",
        "srcdoc",
        "srcset",
        "formaction",
        "xlink:href",
      ],
      ALLOW_UNKNOWN_PROTOCOLS: false,
    }
  );

  // Read Aloud handlers
  const getPlainText = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handlePlay = () => {
    if (isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
      setIsSpeaking(true);
      return;
    }

    window.speechSynthesis.cancel();
    const text = getPlainText(sanitizedContent);
    if (!text.trim()) return;

    // Chunk text by sentences to prevent Chrome's 15s speech cutoff bug on long text
    const chunks = text.match(/[^.!?]+[.!?]+|\s*[^.!?]+\s*$/g) || [text];
    const validChunks = chunks.map(c => c.trim()).filter(c => c.length > 0);

    if (validChunks.length === 0) return;

    const voices = window.speechSynthesis.getVoices();
    const match = voices.find((v) => v.lang === selectedLang);

    let currentChunkIndex = 0;

    const playNextChunk = () => {
      if (currentChunkIndex >= validChunks.length) {
        setIsSpeaking(false);
        setIsPaused(false);
        return;
      }

      const utterance = new SpeechSynthesisUtterance(validChunks[currentChunkIndex]);
      utterance.lang = selectedLang;
      if (match) utterance.voice = match;
      utterance.rate = 1;
      utterance.pitch = 1;

      utterance.onend = () => {
        currentChunkIndex++;
        playNextChunk();
      };

      utterance.onerror = (e) => {
        console.error("TTS Error:", e);
        // Only stop if it's a real error, not just an intentional cancel
        if (e.error !== 'canceled') {
          setIsSpeaking(false);
          setIsPaused(false);
        }
      };

      window.speechSynthesis.speak(utterance);
    };

    setIsSpeaking(true);
    setIsPaused(false);
    playNextChunk();
  };

  const handlePause = () => {
    window.speechSynthesis.pause();
    setIsPaused(true);
    setIsSpeaking(false);
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setIsPaused(false);
  };

  const refreshSummary = () => {
    setIsSummaryLoading(true);
    setSummaryRefreshKey((key) => key + 1);
  };

  const toggleSummaryFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await summaryFrameRef.current?.requestFullscreen();
      }
    } catch (error) {
      console.warn("Unable to toggle summary fullscreen mode", error);
    }
  };

  return (
    <Fragment>
      {isLoading && (
        <div className={styles["loader"]}>
          <LoaderBig />
        </div>
      )}
      {!isLoading && postsData.post.createDate && (
        <div className={styles["post-main"]}>
          <div className={styles["progress-bar-track"]}>
            <div
              className={styles["progress-bar-fill"]}
              style={{ width: `${scrollProgress}%` }}
            />
          </div>
          <div className={styles["post-sub"]}>
            <div className={styles["title-section"]}>
              <div className={styles["title"]}>
                <h1>{postsData.post.post.title}</h1>
                <p className={styles["desc"]}><strong>Description: </strong> <br />{postsData.post.post.desc}</p>
                <p><strong>Tags: </strong>{postsData.post.post.imgSource}</p>
                <div className={styles["date"]}>
                  <p><strong>Published On:</strong> {postsData.post.createDate}</p>
                  {postsData.post.updateDate && (
                    <p>Updated: {postsData.post.updateDate}</p>
                  )}

                  <div className={styles["views"]}>
                    <img
                      width="20"
                      height="20"
                      src="https://img.icons8.com/ios/50/visible--v1.png"
                      alt="visible--v1"
                    />
                    <p> {postsData.post.post.views}</p>
                  </div>
                </div>
                <p className={styles["name"]}>
                  <strong>Author:</strong> <span> {postsData.post.post.user.name}</span>
                </p>
                {/* Social Share Section */}
                <div className={styles["share-section"]}>
                  <p className={styles["share-label"]}>Share this post</p>
                  <div className={styles["share-buttons"]}>
                    <button
                      id="share-twitter"
                      className={`${styles["share-btn"]} ${styles["share-twitter"]}`}
                      onClick={() => handleShare("twitter")}
                      aria-label="Share on X (Twitter)"
                      title="Share on X"
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </button>
                    <button
                      id="share-facebook"
                      className={`${styles["share-btn"]} ${styles["share-facebook"]}`}
                      onClick={() => handleShare("facebook")}
                      aria-label="Share on Facebook"
                      title="Share on Facebook"
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </button>
                    <button
                      id="share-linkedin"
                      className={`${styles["share-btn"]} ${styles["share-linkedin"]}`}
                      onClick={() => handleShare("linkedin")}
                      aria-label="Share on LinkedIn"
                      title="Share on LinkedIn"
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </button>
                    <button
                      id="share-whatsapp"
                      className={`${styles["share-btn"]} ${styles["share-whatsapp"]}`}
                      onClick={() => handleShare("whatsapp")}
                      aria-label="Share on WhatsApp"
                      title="Share on WhatsApp"
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </button>
                    <button
                      id="share-reddit"
                      className={`${styles["share-btn"]} ${styles["share-reddit"]}`}
                      onClick={() => handleShare("reddit")}
                      aria-label="Share on Reddit"
                      title="Share on Reddit"
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
                      </svg>
                    </button>
                    <button
                      id="share-copy-link"
                      className={`${styles["share-btn"]} ${styles["share-copy"]}`}
                      onClick={handleCopyLink}
                      aria-label="Copy link"
                      title="Copy link"
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                      {copied && <span className={styles["copy-tooltip"]}>Copied!</span>}
                    </button>
                  </div>
                </div>


              </div>
              <div className={styles["image-section"]}>
                <img
                  src={postsData.post.post.image}
                  alt={postsData.post.post.imageName}
                />

              </div>
            </div>
          </div>

          {/* Read Aloud Control Bar */}
          <div className={styles["read-aloud-bar"]}>
            <div className={styles["read-aloud-inner"]}>
              <div className={styles["read-aloud-label"]}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                </svg>
                <span>Read Aloud</span>
              </div>
              <div className={styles["read-aloud-controls"]}>
                <select
                  id="tts-language-select"
                  className={styles["lang-select"]}
                  value={selectedLang}
                  onChange={(e) => {
                    setSelectedLang(e.target.value);
                    handleStop();
                  }}
                  title="Select language"
                >
                  {availableVoices.map((v) => (
                    <option key={v.lang} value={v.lang}>
                      {v.lang} — {v.name.length > 25 ? v.name.slice(0, 25) + "…" : v.name}
                    </option>
                  ))}
                </select>
                {!isSpeaking && !isPaused && (
                  <button
                    id="tts-play"
                    className={`${styles["tts-btn"]} ${styles["tts-play"]}`}
                    onClick={handlePlay}
                    aria-label="Play"
                    title="Play"
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </button>
                )}
                {isSpeaking && (
                  <button
                    id="tts-pause"
                    className={`${styles["tts-btn"]} ${styles["tts-pause"]}`}
                    onClick={handlePause}
                    aria-label="Pause"
                    title="Pause"
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
                    </svg>
                  </button>
                )}
                {isPaused && (
                  <button
                    id="tts-resume"
                    className={`${styles["tts-btn"]} ${styles["tts-play"]}`}
                    onClick={handlePlay}
                    aria-label="Resume"
                    title="Resume"
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </button>
                )}
                {(isSpeaking || isPaused) && (
                  <button
                    id="tts-stop"
                    className={`${styles["tts-btn"]} ${styles["tts-stop"]}`}
                    onClick={handleStop}
                    aria-label="Stop"
                    title="Stop"
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <rect x="6" y="6" width="12" height="12" rx="1" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          <section
            className={`${styles["quick-read-embed"]} ${isSummaryExpanded ? styles["quick-read-expanded"] : ""}`}
            aria-label="Quick Read AI Summary powered by Gemini"
          >
            <div
              className={`${styles["quick-read-frame"]} ${isSummaryLoading ? styles["quick-read-loading"] : styles["quick-read-ready"]}`}
              ref={summaryFrameRef}
            >
              <div className={styles["quick-read-floating-actions"]} aria-label="Summary controls">
                  <button type="button" onClick={refreshSummary} aria-label="Refresh AI summary" title="Refresh summary">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M20 11a8.1 8.1 0 0 0-14.8-4L3 9" />
                      <path d="M3 4v5h5M4 13a8.1 8.1 0 0 0 14.8 4L21 15" />
                      <path d="M21 20v-5h-5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsSummaryExpanded((expanded) => !expanded)}
                    aria-label={isSummaryExpanded ? "Collapse AI summary" : "Expand AI summary"}
                    title={isSummaryExpanded ? "Collapse summary" : "Expand summary"}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      {isSummaryExpanded ? (
                        <path d="m9 9 6 6M15 9l-6 6" />
                      ) : (
                        <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M21 16v5h-5" />
                      )}
                    </svg>
                  </button>
                  <a
                    className={styles["quick-read-control-link"]}
                    href={quickReadUrl.toString()}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open AI summary in a new tab"
                    title="Open in new tab"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M14 5h5v5M19 5l-8 8" />
                      <path d="M18 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
                    </svg>
                  </a>
                  <button type="button" onClick={toggleSummaryFullscreen} aria-label="Open AI summary fullscreen" title="Fullscreen">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M21 16v5h-5" />
                    </svg>
                  </button>
              </div>
              <iframe
                key={summaryRefreshKey}
                src={quickReadUrl.toString()}
                title="Quick Read AI Summary powered by Gemini"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={() => setIsSummaryLoading(false)}
              />
            </div>
          </section>

          <div className={styles["text-section-main"]}>
            <div
              className={styles["text-section"]}
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            ></div>
          </div>


        </div>
      )}
    </Fragment>
  );
};

export default Singlepost;
